import * as React from 'react';
import {withStyles, WithStyles} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { ChatStyle } from './Chat.style';
import ChatMessage from './ChatMessage';
import { IChat, IChatMessage, IRequestChatMessages, IChatReceipt, IChatParticipant, 
  ChatType, IChatReaction, ICreateChatMessage, ICreateChatMessageAction, 
  ChatMsgStatus, IRequestMessageReadInfo} from '@app-types/websocket/chat.types';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { isSameDay } from '@helpers/DateHelper';
import ChatInfoMessage from './ChatInfoMessage';
import * as ChatActions from '@services/chat/chat.actions';
import * as CallActions from '@services/calls/calls.actions';
import { ISuggestion } from '@app-types/websocket/suggestion.types';
import ChatSuggestionMessage from './ChatSuggestionMessage';
import ShowChanges from '@components/calendar2/suggestion/ShowChanges';
import { connect } from 'react-redux';
import ChatCallMessage from './ChatCallMessage';
import { ICall } from '@app-types/websocket/calls.types';
import ChatToxicSheet from './ChatToxicSheet';
import { isReaction } from '@services/chat/chatReply';


const tn: TranslateNew = TranslateNew.getInstance();
const TARGET_CONTEXT_MESSAGES = 50;
const TARGET_CONTEXT_MESSAGES_AFTER = 25;
const TARGET_SCROLL_ATTEMPTS = 12;
const MESSAGE_READ_INFO_START = new Date(2026, 8, 9).getTime();

interface IChatObject {
  messageInTransit?:IChatMessage;
  suggestion?:ISuggestion;
  message?:IChatMessage;
  sendingTime:number;
  senderId:number;
  call?:ICall;
}

interface IProps extends WithStyles<typeof ChatStyle> {
  userId:number;
  family:IFamilyMember[];
  chatId:number|undefined;
  chats:IChat[];
  suggestions:ISuggestion[];
  isAuthenticated:boolean;
  showAllMessages:boolean;
  isOpening?:boolean;
  targetMessageId?:number;
  calls:ICall[];
  requestChatMessages: (request:IRequestChatMessages) => void;
  requestMessageReadInfo: (request:IRequestMessageReadInfo) => void;
  sendChatReadReceipt: (receipt:IChatReceipt) => void;
  sendReaction:(data: ICreateChatMessage) => void;
  toxicAction:(data: ICreateChatMessageAction) => void;
  setEditMessage:(msg:string) => void;
  setReplyTo:(messageId:number) => void;
  callHistory:(id:number) => void;
}

interface IState {
  openEditChat:boolean;
  showSuggestionDetails:ISuggestion|undefined;
  reactTo:number|undefined;
  chatMessages:JSX.Element[] ;
  msgsToRender:number;
  highestMessageId:number;
}

class ChatRoom extends React.Component<IProps, IState> {
  private containerRef:React.RefObject<HTMLDivElement>=React.createRef();
  private bottomRef:React.RefObject<HTMLDivElement>=React.createRef();
  private scrollAddedMoreDaysTime:number = 0;
  private prevScrollPos:number = 0;
  private sentReadReceipt:number = 0;
  private lastMessagesLoaded = false;
  private requestedTargetContext:string|undefined;
  private scrollTargetTimeout:number|undefined;

  constructor(props:IProps) {
    super(props);

    const messages = this.buildMessages(props, undefined, 40);
    this.state = {
      openEditChat:false,
      showSuggestionDetails:undefined,
      reactTo:undefined,
      chatMessages:messages.msgs,
      msgsToRender:40,
      highestMessageId:messages.highestMessageId,
    };
  }

  public componentDidMount() {
    if (this.props.isOpening) {
      return;
    }
    this.activateChat();
    this.scrollToTargetMessage();
  }

  public componentDidUpdate(oldProps:IProps) {
    // Hold the cached preview steady while the compositor slides the pane.
    // The first update after opening picks up every refresh received meanwhile.
    if (this.props.isOpening) {
      return;
    }
    const finishedOpening = !!oldProps.isOpening;
    const chatChanged = this.props.chats.find((chat) => chat.id === this.props.chatId)
      !== oldProps.chats.find((chat) => chat.id === oldProps.chatId);
    if (this.props.isAuthenticated !== oldProps.isAuthenticated
      || this.props.chatId !== oldProps.chatId
      || this.props.targetMessageId !== oldProps.targetMessageId
      || finishedOpening) {
      this.activateChat();
    }
    // Everything buildMessages renders from must be listed here, or the cached
    // JSX in state.chatMessages goes stale when that prop updates.
    if (this.props.chatId !== oldProps.chatId
      || chatChanged
      || this.props.calls !== oldProps.calls
      || this.props.suggestions !== oldProps.suggestions
      || this.props.family !== oldProps.family
      || this.props.userId !== oldProps.userId
      || this.props.targetMessageId !== oldProps.targetMessageId
      || finishedOpening) {
      // A data refresh must not close an open reaction/image overlay; only a
      // chat switch or a target jump may change it.
      const nextReactTo = (this.props.chatId !== oldProps.chatId || this.props.targetMessageId !== oldProps.targetMessageId)
        ? undefined : this.state.reactTo;
      const newState = this.buildMessages(this.props, nextReactTo, this.state.msgsToRender);
      this.setState({chatMessages:newState.msgs, highestMessageId:newState.highestMessageId, reactTo:nextReactTo}, () => {
        this.scrollToTargetMessage();
      });
    } else if (this.props.showAllMessages !== oldProps.showAllMessages ) {
        const newState = this.buildMessages(this.props, this.state.reactTo, this.state.msgsToRender);
        this.setState({chatMessages:newState.msgs, highestMessageId:newState.highestMessageId}, () => {
          this.scrollToTargetMessage();
        });
    }
    if (this.props.isAuthenticated && chatChanged && !finishedOpening) {
      this.loadLatest();
      this.readReceipt();
    }
  }

  public componentWillUnmount() {
    if (this.scrollTargetTimeout !== undefined) {
      window.clearTimeout(this.scrollTargetTimeout);
    }
  }

  public render() {
    const {classes, } = this.props;
    const chat = this.props.chats.find((c) => c.id === this.props.chatId);

    return (
      <div id='chatroom' className={classes.chatMessages} ref={this.containerRef} onScroll={(e) => this.scrollHandler(e.currentTarget)}
        style={(this.state.reactTo&&(this.state.reactTo===this.state.highestMessageId || this.state.reactTo > 100000))?{paddingBottom:"25px"}:{}}
      >
          <div className={classes.chatMessagesBlackout} 
            onClick={() => { if (this.state.reactTo !== undefined) {
              this.setState({chatMessages:this.buildMessages(this.props, undefined, this.state.msgsToRender).msgs, reactTo:undefined});
            }}}
            style={this.state.reactTo===undefined?{pointerEvents:'none', opacity:0}:{}}/>
          <div ref={this.bottomRef} style={{minWidth: '20px', minHeight: '10px'}}/>
          {this.state.chatMessages}
          <ShowChanges // this is a popup to show changes of selected change
            open={this.state.showSuggestionDetails !== undefined}
            close={() => this.setState({showSuggestionDetails:undefined})}
            suggestion={this.state.showSuggestionDetails}
          />
          <ChatToxicSheet messagesInTransit={chat ? chat.messagesInTransit : undefined}
            edit={(msg, edit) => this.haltInTransitMessage(msg, edit)}
            send={(msg) => this.sendInTransitMessage(msg)}/>
      </div>);
  }

  private activateChat() {
    if (this.props.isAuthenticated && this.props.chatId !== undefined) {
      if (this.props.targetMessageId !== undefined) {
        this.resetChatScrollState();
        this.loadTargetContext();
        this.readReceipt();
      } else {
        this.scrollToBottom();
        this.loadLatest();
      }
      const chat = this.props.chats.find((c) => c.id === this.props.chatId);
      if (chat && chat.chatType === ChatType.SCHEDULE_CHAT) {
        const messages = this.getChatMessages(chat);
        const oldestId = messages.length > 0 ? messages[0].id : 1;
        const newestId = messages.length > 0 ? messages[messages.length - 1].id : 1;
        this.props.requestChatMessages({chatId:chat.id,
          startId:oldestId > 1 ? oldestId : Math.max(newestId, 1), numMsgsBack:20});
      }
    }
  }

  private sendReaction(reaction:string) {
    const msgId = this.state.reactTo;
    if (msgId !== undefined) {
      this.setState({chatMessages:this.buildMessages(this.props, undefined, this.state.msgsToRender).msgs, reactTo:undefined});
      if (this.props.chatId !== undefined) {
        this.props.sendReaction({
          chatId: this.props.chatId,
          msg: reaction,
          questionId: msgId,
        });  
      }
    }
  }

  private buildMessages(props: IProps, reactTo:number|undefined, messagesToRender:number):{msgs:JSX.Element[], highestMessageId:number} {
    const reactionsMap:{[key:string]:IChatReaction[]} = {};
    const messagesById:{[key:string]:IChatMessage} = {}; // what replies quote
    let highestMessageId = 0;

    const chat:IChat|undefined = (props.chats && props.chatId)?props.chats.find((c) => c.id === props.chatId):undefined;
    const allMessages:IChatMessage[] = this.getChatMessages(chat);
    const targetLocationLoaded = this.isTargetLocationLoaded(allMessages, props.targetMessageId);
    const showAllMessages = !!props.showAllMessages && !props.isOpening;
    let messages:IChatMessage[] = props.targetMessageId !== undefined
      ? targetLocationLoaded
        ? this.getTargetContextMessages(allMessages, props.targetMessageId)
        : []
      : allMessages;

    // Select the window before building JSX, formatting dates or looking up
    // senders/calls. One extra message preserves grouping at the oldest edge.
    const visibleMessages:IChatMessage[] = [];
    const limit = props.targetMessageId !== undefined || showAllMessages ? Infinity : messagesToRender + 1;
    for (let index = messages.length - 1; index >= 0 && visibleMessages.length < limit; index--) {
      const message = messages[index];
      if (!isReaction(message) && (this.getMessageText(message).trim() !== ''
        || message.fileDesc !== undefined || message.callId !== undefined)) {
        visibleMessages.push(message);
      }
    }
    messages = visibleMessages.reverse();
    const visibleIds = new Set(messages.map((message) => message.id));

    if (allMessages.length > 0) {
      for (const m of allMessages) {
        if (isReaction(m) && m.questionId !== undefined) {
          if (!visibleIds.has(m.questionId)) {
            continue;
          }
          let reactionList:IChatReaction[]|undefined = reactionsMap[""+m.questionId];
          if (reactionList === undefined) {
            reactionList = [];
            reactionsMap[""+m.questionId] = reactionList;
          }
          reactionList.push({chatId:m.chatId, id:m.id, senderId:m.senderId, sendingTime:m.sendingTime, msg:this.getMessageText(m)});
        } else {
          messagesById[""+m.id] = m;
          if (m.id > highestMessageId) {
            highestMessageId = m.id;
          }
        }
      }
    }

    const chatMessages:JSX.Element[] = [];
    
    let chatObjects:IChatObject[] = [];

    if (messages.length > 0) {
      chatObjects = messages.map((m) => {
        const call = ((m.callId !== undefined) ? props.calls.find((c) => c.id === m.callId):undefined);
        const obj:IChatObject = {sendingTime:m.sendingTime, message:m, senderId:m.senderId, call}; 
        return obj
      });
    }

    if (chat && chat.chatType && chat.chatType === ChatType.SCHEDULE_CHAT && chat.referenceObject !== undefined && props.suggestions !== undefined) {
      const sortedSuggestions = props.suggestions.slice().sort((a, b) => a.publishTime - b.publishTime);
      const filteredSuggestions = Number.isFinite(limit) ? sortedSuggestions.slice(-limit) : sortedSuggestions;
      if (filteredSuggestions.length > 0) {
        chatObjects = chatObjects.concat(filteredSuggestions.map((s) => {const obj:IChatObject = ({
          sendingTime: s.publishTime,
          suggestion:s,
          senderId:s.creatorId,
        }); return obj;}));
        chatObjects.sort((a,b) => a.sendingTime - b.sendingTime);  
      }
    }

    if (chat && chat.messagesInTransit && chat.messagesInTransit.length > 0) {
      chatObjects = chatObjects.concat(chat.messagesInTransit.map((s) => {const obj:IChatObject = ({
        sendingTime: s.sendingTime,
        messageInTransit:s,
        senderId:s.senderId,
      }); return obj;}));
    }

    if (chat && chatObjects.length > 0) {
      const lastReadMarks:{[key: string]: number[]} = {};

      if (chat.participants) {
        const participants = chat.participants ? chat.participants : {};
        for (const participantIdString of Object.keys(participants)) {
          const participant:IChatParticipant = participants[participantIdString];
          const participantId:number = Number(participantIdString);
          if (participant !== undefined && participantId !== this.props.userId && Number.isFinite(participantId) && participant.lastRead > 0) {
            if (lastReadMarks[""+participant.lastRead] === undefined) {
              lastReadMarks[""+participant.lastRead] = [];
            }
            lastReadMarks[""+participant.lastRead].push(participantId);
          }
        }
      }

      let previousSender:number|undefined;
      let previousTime:Date|undefined;

      for (let index = 0; index < chatObjects.length; index++) {
        const chatObject:IChatObject = chatObjects[index];

        if (chatObject.message !== undefined 
          && this.getMessageText(chatObject.message).trim() === "" && chatObject.message.fileDesc === undefined
            && chatObject.message.callId === undefined) {continue;}

        let nextSender:number|undefined;

        const time = new Date(chatObject.sendingTime);
        
        if (index + 1 < chatObjects.length) {
          const nextChatMessage:IChatObject = chatObjects[index+1];
          const nextTime = new Date(nextChatMessage.sendingTime);
          if (isSameDay(time, nextTime)) {
            nextSender = nextChatMessage.senderId;
          } else {
            nextSender = undefined;
          }
        }


        if (previousTime === undefined || !isSameDay(time, previousTime)) {
          chatMessages.unshift(
            <ChatInfoMessage key={"t"+chatObject.sendingTime} 
              message={tn.formatDateYearMonthDay(time)} />);
          previousSender = undefined;
        }
        previousTime = time;
        
        if (chatObject.message !== undefined) {
          const chatMessage = chatObject.message;

          const reactions:IChatReaction[]|undefined = reactionsMap[""+chatMessage.id];

          if (chatMessage.callId !== undefined) {
            chatMessages.unshift(<div id={this.getMessageElementId(chatMessage.id)} key={"c"+chatMessage.id}>
              <ChatCallMessage family={props.family} call={chatObject.call}
                callId={chatMessage.callId} callHistory={(id) => this.props.callHistory(id)}/>
            </div>);
          } else if (!isReaction(chatMessage)) {
            chatMessages.unshift(
              <ChatMessage key={""+chatMessage.id} 
                chatId={chat.id}
                chatMessage={chatMessage} 
                chatReactions={reactions}
                replyToMessage={chatMessage.questionId !== undefined ? messagesById[""+chatMessage.questionId] : undefined}
                userId={props.userId}
                haltMessage={(id, rewrite) => alert("not implemented")}
                sendMessage={(id) => alert("not implemented")} 
                reactTo={() => this.selectMessage(chatMessage)}
                reply={() => this.startReply(chatMessage.id)}
                addReaction={chatMessage.id === reactTo}
                firstMessage={chatMessage.senderId !== previousSender}
                lastMessage={chatMessage.senderId !== nextSender}
                sendReaction={(reaction) => this.sendReaction(reaction)}
                clearReaction={() => this.reactTo(undefined)}
                sender={props.family.find((m) =>m.id===chatMessage.senderId)}/>);  
          }
  
          if (lastReadMarks[""+chatMessage.id] !== undefined) {
            chatMessages.unshift(<ChatInfoMessage key={"r"+chatMessage.id} 
              message={tn.lang.chatSeen(tn.familyMemberNames(lastReadMarks[""+chatMessage.id], this.props.family))} />);
          }
        } else if (chatObject.messageInTransit !== undefined && chatObject.messageInTransit.status === ChatMsgStatus.AWAIT_RESULT) {
          const chatMessage = chatObject.messageInTransit;
          chatMessages.unshift(
            <ChatMessage key={""+chatMessage.id} 
              chatId={chat.id}
              chatMessage={chatMessage} 
              chatReactions={undefined}
              replyToMessage={chatMessage.questionId !== undefined ? messagesById[""+chatMessage.questionId] : undefined}
              userId={props.userId}
              inTransit={true}
              haltMessage={(id, rewrite) => this.haltInTransitMessage(id, rewrite)}
              sendMessage={(id) => this.sendInTransitMessage(id)}
              reactTo={() => this.reactTo(chatMessage.id)}
              addReaction={chatMessage.id === reactTo}
              firstMessage={chatMessage.senderId !== previousSender}
              lastMessage={chatMessage.senderId !== nextSender}
              sendReaction={(reaction) => {return;}}
              clearReaction={() => this.reactTo(undefined)}
              sender={props.family.find((m) =>m.id===chatMessage.senderId)}/>);  
        } else if (chatObject.suggestion !== undefined) {
          const chatSuggestion = chatObject.suggestion;
          chatMessages.unshift(
            <ChatSuggestionMessage key={"s"+chatSuggestion.id} 
              suggestion={chatSuggestion} 
              showSuggestionDetails={() => this.setState({showSuggestionDetails:chatSuggestion})}
              userId={props.userId} 
              family={props.family}/>);
        }

        previousSender = chatObject.senderId;
      }

      if (chat.hidden && chat.hidden.length > 0) {
        chatMessages.unshift(<ChatInfoMessage key={"archived"} 
          message={tn.lang.chatArchivedBy(tn.familyMemberNames(chat.hidden, this.props.family))} />);
      }

    } else {
      chatMessages.push(<ChatInfoMessage message={(!chat && props.chatId !== undefined) 
        || (props.targetMessageId !== undefined && !targetLocationLoaded) 
        ? tn.lang.initializing 
        : tn.lang.noMessagesYet} key="nomsg"/>)
    }

    if (props.targetMessageId !== undefined || showAllMessages) {
      return {msgs:chatMessages, highestMessageId};
    } else {
      return {msgs:chatMessages.slice(0, messagesToRender), highestMessageId};
    }
    
  }

  private sendInTransitMessage(msg:IChatMessage) {
    this.props.toxicAction({send:true, id:msg.id});
    this.reactTo(undefined);
  }

  private haltInTransitMessage(msg:IChatMessage, edit:"ORIGINAL"|"AISUGGESTION"|"EMPTY") {
    this.props.toxicAction({send:false, id:msg.id});
    if (edit === "AISUGGESTION" && msg.rewrite) {
      this.props.setEditMessage(msg.rewrite);
    } else if (edit === "ORIGINAL") {
      this.props.setEditMessage(this.getMessageText(msg));
    } else if (edit === "EMPTY") {
      this.props.setEditMessage("");
    }
    // A reply that goes back to the input for editing stays a reply.
    if (edit !== "EMPTY" && msg.questionId !== undefined && msg.questionId > 0) {
      this.props.setReplyTo(msg.questionId);
    }
    this.reactTo(undefined);
  }

  private startReply(messageId:number) {
    this.reactTo(undefined);
    this.props.setReplyTo(messageId);
  }

  private selectMessage(message:IChatMessage) {
    const chatId = this.props.chatId;
    let requestReadInfo = false;
    this.setState((state) => {
      requestReadInfo = state.reactTo !== message.id && chatId !== undefined && chatId > 0 && message.id > 0
        && Number.isFinite(message.sendingTime) && message.sendingTime >= MESSAGE_READ_INFO_START;
      return {
        reactTo:message.id,
        chatMessages:this.buildMessages(this.props, message.id, state.msgsToRender).msgs,
      };
    }, () => {
      if (requestReadInfo && chatId !== undefined) {
        this.props.requestMessageReadInfo({chatId, msgId:message.id});
      }
    });
  }

  private reactTo(msgId:number|undefined) {
    this.setState({reactTo:msgId, chatMessages:this.buildMessages(this.props, msgId, this.state.msgsToRender).msgs});
  }

  private scrollHandler(e:HTMLDivElement) {
    if (this.props.isOpening) {
      return;
    }

    if (e.scrollTop < this.prevScrollPos) {
      if ((e.scrollHeight - (e.clientHeight - e.scrollTop)) < 500 && Date.now() - this.scrollAddedMoreDaysTime > 1000) {
        this.scrollAddedMoreDaysTime = Date.now();
        this.loadHistory();
      }  
    }
    this.prevScrollPos = e.scrollTop;
  }

  private loadHistory() {
    if (this.props.chats && this.props.chatId) {
      const chatMessages = this.props.chats.find((c) => c.id === this.props.chatId) 
      const messages = this.getChatMessages(chatMessages);
      if (chatMessages && messages.length > 0) {

        this.setState({msgsToRender:this.state.msgsToRender+20, 
          chatMessages:this.buildMessages(this.props, undefined, this.state.msgsToRender+20).msgs});

        const mostRecentMessageId = messages[messages.length-1].id;
        const msgsToCheck = messages.length;
        let msgIdOfMesgBefore = mostRecentMessageId + 1;
        for (let index = 0; index < msgsToCheck; index++) {
          const msgId = messages[messages.length-(1+index)].id;
          if (msgId !== (msgIdOfMesgBefore-1)) { // we have a missing message!
            this.props.requestChatMessages({
              chatId:this.props.chatId,   
              startId: msgIdOfMesgBefore,
              numMsgsBack: 20});
            break;
          }
          msgIdOfMesgBefore = msgId;
        }
        const firstMessageId = messages[0].id;
        if (firstMessageId !== 0 && firstMessageId !== 1) {
          this.props.requestChatMessages({
            chatId:this.props.chatId,   
            startId: msgIdOfMesgBefore,
            numMsgsBack: 20});
        }
    }
    }
  }

  private loadLatest() {
    if (this.props.chats && this.props.chatId && !this.lastMessagesLoaded) {
      const chatMessages = this.props.chats.find((c) => c.id === this.props.chatId) 
      const messages = this.getChatMessages(chatMessages);
      if (chatMessages && messages.length > 0) {
        // const firstMessageId = chatMessages.messages[0].id;
        const mostRecentMessageId = messages[messages.length-1].id;
        let lastMessagesLoadedTest:boolean = true;
        if (messages.length < Math.min(mostRecentMessageId, 20)) {
          lastMessagesLoadedTest = false;
        } else {
          const messagesToCheck = Math.min(messages.length, 20);
          let msgIdOfMesgBefore = mostRecentMessageId + 1;
          for (let index = 0; index < messagesToCheck; index++) {
            const msgId = messages[messages.length-(1+index)].id;
            if (msgId !== (msgIdOfMesgBefore-1)) { // we have a missing message!
              lastMessagesLoadedTest = false;
            }
            msgIdOfMesgBefore = msgId;
          }
        }
        if (!lastMessagesLoadedTest) {
          this.props.requestChatMessages({
            chatId:this.props.chatId,   
            startId: mostRecentMessageId,
            numMsgsBack: 20});
        }
        this.lastMessagesLoaded = lastMessagesLoadedTest;
      }
    }
    
  }

  private loadTargetContext() {
    if (this.props.chatId === undefined || this.props.targetMessageId === undefined) {
      return;
    }

    const requestKey = `${this.props.chatId}:${this.props.targetMessageId}`;
    if (this.requestedTargetContext === requestKey) {
      return;
    }

    this.requestedTargetContext = requestKey;
    this.props.requestChatMessages({
      chatId:this.props.chatId,
      startId:this.props.targetMessageId + TARGET_CONTEXT_MESSAGES_AFTER,
      numMsgsBack:TARGET_CONTEXT_MESSAGES,
    });
  }

  private scrollToBottom() {
    this.resetChatScrollState();
    if (this.containerRef.current) {
      // Only scroll the reversed message list, not the fixed pane's ancestors.
      this.containerRef.current.scrollTop = 0;
    }
    this.readReceipt();  
  }

  private scrollToTargetMessage(attempt:number = 0) {
    if (this.props.targetMessageId === undefined || this.containerRef.current === null) {
      return;
    }

    const chat = this.props.chats.find((c) => c.id === this.props.chatId);
    if (!this.isTargetLocationLoaded(this.getChatMessages(chat), this.props.targetMessageId)) {
      return;
    }

    const targetElement = document.getElementById(this.getMessageElementId(this.props.targetMessageId));
    if (targetElement !== null && this.containerRef.current.contains(targetElement)) {
      targetElement.scrollIntoView({
        behavior: attempt === 0 ? "auto" : "smooth",
        block: "center",
      });
      return;
    }

    if (attempt >= TARGET_SCROLL_ATTEMPTS) {
      return;
    }

    if (this.scrollTargetTimeout !== undefined) {
      window.clearTimeout(this.scrollTargetTimeout);
    }
    this.scrollTargetTimeout = window.setTimeout(() => {
      this.scrollToTargetMessage(attempt + 1);
    }, 100);
  }

  private isTargetLocationLoaded(messages:IChatMessage[], targetMessageId:number|undefined):boolean {
    if (targetMessageId === undefined) {
      return false;
    }

    const messageIds:{[key:string]:boolean} = {};
    let hasTarget = false;
    let newestLoadedMessageId = targetMessageId;

    for (const message of messages) {
      if (isReaction(message)) {
        continue;
      }
      messageIds[""+message.id] = true;
      if (message.id === targetMessageId) {
        hasTarget = true;
      }
      if (message.id > newestLoadedMessageId) {
        newestLoadedMessageId = message.id;
      }
    }

    if (!hasTarget) {
      return false;
    }

    const oldestExpectedMessageId = Math.max(1, targetMessageId - (TARGET_CONTEXT_MESSAGES - TARGET_CONTEXT_MESSAGES_AFTER - 1));
    const newestExpectedMessageId = Math.min(targetMessageId + TARGET_CONTEXT_MESSAGES_AFTER, newestLoadedMessageId);

    for (let messageId = oldestExpectedMessageId; messageId <= newestExpectedMessageId; messageId++) {
      if (messageIds[""+messageId] !== true) {
        return false;
      }
    }

    return true;
  }

  private getTargetContextMessages(messages:IChatMessage[], targetMessageId:number):IChatMessage[] {
    const oldestMessageId = Math.max(1, targetMessageId - (TARGET_CONTEXT_MESSAGES - TARGET_CONTEXT_MESSAGES_AFTER - 1));
    const newestMessageId = targetMessageId + TARGET_CONTEXT_MESSAGES_AFTER;

    return messages.filter((message) => {
      if (isReaction(message) && message.questionId !== undefined) {
        return message.questionId >= oldestMessageId && message.questionId <= newestMessageId;
      }
      return message.id >= oldestMessageId && message.id <= newestMessageId;
    });
  }

  private getMessageElementId(messageId:number):string {
    return `chat-message-${messageId}`;
  }

  private resetChatScrollState() {
    this.scrollAddedMoreDaysTime = 0;
    this.prevScrollPos = 0;
    this.sentReadReceipt = 0;
    this.lastMessagesLoaded = false;
  }

  private readReceipt() {
    if (this.props.chatId && this.props.chats) {
      const chat = this.props.chats.find((c) => c.id === this.props.chatId);
      const messages = this.getChatMessages(chat);
      if (chat && messages.length > 0 && chat.participants) {
        const participants = chat.participants ? chat.participants : {};
        const me = participants[""+this.props.userId];
        const lastMessage = messages[messages.length - 1];
        if (me) {
            if (lastMessage.id !== me.lastRead && this.sentReadReceipt !== lastMessage.id) {
              this.sentReadReceipt = lastMessage.id;
              this.props.sendChatReadReceipt({chatId:this.props.chatId});
            }
        }  
      }
    }
  }

  private getChatMessages(chat:IChat|undefined):IChatMessage[] {
    return chat && Array.isArray(chat.messages) ? chat.messages : [];
  }

  private getMessageText(message:IChatMessage):string {
    return typeof message.msg === "string" ? message.msg : "";
  }

}

const mapDispatchToProps = {
  requestMessageReadInfo: ChatActions.requestMessageReadInfo,
  sendReaction: ChatActions.createChatMessage,
  toxicAction: ChatActions.createChatMessageToxicCheckedAction,
  callHistory: CallActions.callHistory,
};

export default connect(undefined, mapDispatchToProps)(withStyles(ChatStyle)(ChatRoom));



// WEBPACK FOOTER //
// ./src/components/chat/ChatRoom.tsx